import { useMemo } from 'react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { hasSchemaACL, isWeaveUser } from '@frontend/auth-helpers';
import { useAllAccessScopeStore, useAppScopeStore } from '@frontend/scope';

type Props = {
  isAllAccessScopeEnabled?: boolean;
  isDemoAccount?: boolean;
  permissions: Permission[];
};

type UseHasAclAccess = {
  allLocationsHaveAclAccess: boolean;
  locationIdsWithAclAccess: string[];
  noLocationsHaveAclAccess: boolean;
  someLocationsHaveAclAccess: boolean;
};

export const useHasAclAccess = ({
  isAllAccessScopeEnabled,
  isDemoAccount,
  permissions = [],
}: Props): UseHasAclAccess => {
  const { selectedLocationIds: selectedLocationIdsInGLS = [] } = useAppScopeStore();
  const { accessibleLocationIds: allAccessibleLocationIds = [] } = useAllAccessScopeStore();

  // To avoid load and bottleneck on DB, for weave internal users we need to only use locations selected in the GLS.
  const locationIds = isWeaveUser() || !isAllAccessScopeEnabled ? selectedLocationIdsInGLS : allAccessibleLocationIds;

  const locationIdsWithAclAccess = useMemo(() => {
    if (isDemoAccount) return locationIds;
    return locationIds.filter((locationId) => permissions.every((permission) => hasSchemaACL(locationId, permission)));
  }, [isDemoAccount, locationIds, permissions]);

  return {
    allLocationsHaveAclAccess: locationIds.length > 0 && locationIds.length === locationIdsWithAclAccess.length,
    locationIdsWithAclAccess,
    noLocationsHaveAclAccess: locationIdsWithAclAccess.length === 0,
    someLocationsHaveAclAccess: locationIdsWithAclAccess.length > 0,
  };
};
